import React, { useState, useEffect } from 'react';
import { Container, Button, Modal, Alert, Table, Form, Badge, Spinner, Row, Col } from "react-bootstrap";
import { LanguageProvider } from '../../../core/language-provider';
import { ApiManager } from '../../../core/api-manager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { settings } from '../../../config/settings';
import './ImportTable.css';

export default function ModalEntityImport({ entity, props, entityPreferences, status, close, onSuccess }) {

    const [step, setStep] = useState(1); // 1: upload, 2: preview, 3: result
    const [loading, setLoading] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [error, setError] = useState('');
    const [showOnlyErrors, setShowOnlyErrors] = useState(false);

    // Check if entity is a SQL view (starts with gyv_)
    const isSqlView = entity && entity.startsWith('gyv_');

    useEffect(() => {
        if (!status) {
            // Reset when modal closes
            setStep(1);
            setFileData(null);
            setParsedData(null);
            setImportResult(null);
            setError('');
        }
    }, [status]);

    function closeModal() {
        close();
    }

    // Download template
    async function downloadTemplate() {
        setLoading(true);
        setError('');
        
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/utils/import_download_template', {
                entity: entity
            });
            
            if (response.success === 1) {
                // Create download link
                const binaryString = atob(response.data.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: response.data.mimetype });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = response.data.filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                setError(response.message || 'Failed to download template');
            }
        } catch (e) {
            setError('Error downloading template');
        }
        
        setLoading(false);
    }

    // Handle file upload
    function handleFileUpload(e) {
        const file = e.target.files[0];
        if (!file) return;
        
        // Check file type
        const validTypes = ['.csv', '.xlsx', '.xls'];
        const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        
        if (!validTypes.includes(fileExt)) {
            setError('Please upload a CSV or Excel file');
            return;
        }
        
        setError('');
        const reader = new FileReader();
        
        reader.onload = (e) => {
            const content = e.target.result;
            // Convert to base64
            const base64 = btoa(String.fromCharCode(...new Uint8Array(content)));
            setFileData({
                name: file.name,
                content: base64,
                type: fileExt
            });
            parseFile(base64);
        };
        
        reader.readAsArrayBuffer(file);
    }

    // Parse uploaded file
    async function parseFile(fileContent) {
        setLoading(true);
        setError('');
        
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/utils/import_parse_file', {
                entity: entity,
                fileContent: fileContent
            });
            
            if (response.success === 1) {
                setParsedData(response.data);
                setStep(2);
            } else {
                setError(response.message || 'Failed to parse file');
                if (response.errors) {
                    setError(error + '\n' + response.errors.join('\n'));
                }
            }
        } catch (e) {
            setError('Error parsing file');
        }
        
        setLoading(false);
    }

    // Execute import
    async function executeImport() {
        if (!parsedData || !parsedData.rows) return;
        
        setLoading(true);
        setError('');
        
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/utils/import_execute', {
                entity: entity,
                rows: parsedData.rows
            });
            
            if (response.success === 1) {
                setImportResult(response.results);
                setStep(3);
                
                // Call onSuccess callback if provided
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                setError(response.message || 'Import failed');
                if (response.results && response.results.errors) {
                    const errorMessages = response.results.errors.map(e => 
                        `Row ${e.row}: ${e.errors.join(', ')}`
                    ).join('\n');
                    setError(error + '\n' + errorMessages);
                }
            }
        } catch (e) {
            setError('Error executing import');
        }
        
        setLoading(false);
    }

    // Reset to start
    function reset() {
        setStep(1);
        setFileData(null);
        setParsedData(null);
        setImportResult(null);
        setError('');
    }

    // Get field type icon
    function getFieldIcon(fieldName, fieldConfig) {
        if (!fieldConfig) return <FontAwesomeIcon icon="question" className="field-icon me-1" />;
        
        const type = fieldConfig.input_type || 'string';
        const iconMap = {
            string: 'font',
            text: 'align-left',
            richText: 'edit',
            number: 'hashtag',
            float: 'calculator',
            currency: 'dollar-sign',
            boolean: 'toggle-on',
            date: 'calendar-day',
            datetime: 'clock',
            time: 'clock',
            option: 'list',
            externalEntity: 'link',
            file: 'file',
            password: 'lock',
            color: 'palette',
            address: 'map-marker-alt'
        };
        
        return <FontAwesomeIcon icon={iconMap[type] || 'question'} className="field-icon me-1" />;
    }

    // Get row status badge
    function getRowStatusBadge(row) {
        if (row.errors && row.errors.length > 0) {
            return <Badge bg="danger">Error</Badge>;
        }
        if (row.warnings && row.warnings.length > 0) {
            return <Badge bg="warning">Warning</Badge>;
        }
        if (row.action === 'update') {
            return <Badge bg="info">Update</Badge>;
        }
        return <Badge bg="success">Insert</Badge>;
    }

    // Get field display value
    function getFieldDisplay(row, fieldName) {
        const value = row.data[fieldName];
        
        // Check if this is a resolved external entity
        if (row._resolved && row._resolved[fieldName]) {
            const resolved = row._resolved[fieldName];
            return (
                <span>
                    {resolved.label}
                    <Badge bg="secondary" className="ms-1">ID: {resolved.id}</Badge>
                </span>
            );
        }
        
        // Regular value
        if (value === null || value === undefined) {
            return <span className="text-muted">-</span>;
        }
        
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        
        return String(value);
    }

    if (props !== null && status !== false) {
        return (
            <>
                <Modal 
                    show={status} 
                    onHide={closeModal} 
                    size={settings.modalDefaultSize && settings.modalDefaultSize.entityImport ? settings.modalDefaultSize.entityImport : "xl"} 
                    scrollable={true}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {LanguageProvider.get('defaults.import')} - {LanguageProvider.get('entity.'+entity)}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isSqlView && (
                            <Container className="text-center p-5">
                                <FontAwesomeIcon icon="exclamation-triangle" size="3x" className="text-warning mb-3" />
                                <h5 className="text-warning">Import Not Available</h5>
                                <p className="text-muted">
                                    Import functionality is not available for SQL views.<br />
                                    This entity ({entity}) is a read-only database view.
                                </p>
                                <Alert variant="info" className="mt-3">
                                    <strong>Note:</strong> SQL views are virtual tables created from database queries and cannot be modified directly.
                                    To add or modify data, please use the underlying base tables.
                                </Alert>
                            </Container>
                        )}
                        
                        {!isSqlView && loading && (
                            <div className="text-center p-5">
                                <Spinner animation="border" />
                                <p className="mt-3">Processing...</p>
                            </div>
                        )}
                        
                        {!isSqlView && !loading && step === 1 && (
                            <>
                                <Container className="mb-4">
                                    <h5>Step 1: Upload File</h5>
                                    <p className="text-muted">
                                        Download the template to see the correct format, then upload your Excel or CSV file with the data to import.
                                    </p>
                                    
                                    <Row className="mb-4">
                                        <Col md={6}>
                                            <Button 
                                                variant="outline-primary" 
                                                onClick={downloadTemplate}
                                                className="w-100"
                                            >
                                                <FontAwesomeIcon icon="download" className="me-2" />
                                                Download Template
                                            </Button>
                                        </Col>
                                    </Row>
                                    
                                    <Form.Group className="mb-3">
                                        <Form.Label>Upload File</Form.Label>
                                        <Form.Control 
                                            type="file" 
                                            accept=".csv,.xlsx,.xls"
                                            onChange={handleFileUpload}
                                        />
                                        <Form.Text className="text-muted">
                                            Accepted formats: CSV, Excel (.xlsx, .xls)
                                        </Form.Text>
                                    </Form.Group>
                                    
                                    {fileData && (
                                        <Alert variant="info">
                                            File loaded: {fileData.name}
                                        </Alert>
                                    )}
                                </Container>
                                
                                <Container className="border rounded p-3 bg-light">
                                    <h6>Instructions:</h6>
                                    <ul className="small">
                                        <li>Leave the ID column empty to create new records</li>
                                        <li>Provide an ID to update existing records</li>
                                        <li>For external references, use the display value (not the ID)</li>
                                        <li>Dates should be in format YYYY-MM-DD</li>
                                        <li>Boolean values can be: 0/1, true/false, yes/no</li>
                                    </ul>
                                </Container>
                            </>
                        )}
                        
                        {!isSqlView && !loading && step === 2 && parsedData && (
                            <>
                                <Container className="mb-4">
                                    <h5>Step 2: Preview & Validate</h5>
                                    <p className="text-muted">
                                        Review the data below. Rows with errors must be fixed before import.
                                    </p>
                                    
                                    <Row className="mb-3">
                                        <Col>
                                            <Badge bg="success">Valid rows: {parsedData.rows.filter(r => !r.errors || r.errors.length === 0).length}</Badge>
                                            {' '}
                                            <Badge bg="danger">Errors: {parsedData.rows.filter(r => r.errors && r.errors.length > 0).length}</Badge>
                                            {' '}
                                            <Badge bg="info">Updates: {parsedData.rows.filter(r => r.action === 'update').length}</Badge>
                                            {' '}
                                            <Badge bg="primary">Inserts: {parsedData.rows.filter(r => r.action === 'insert').length}</Badge>
                                        </Col>
                                    </Row>
                                    
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Check
                                                type="switch"
                                                label="Show only rows with errors"
                                                checked={showOnlyErrors}
                                                onChange={(e) => setShowOnlyErrors(e.target.checked)}
                                            />
                                        </Col>
                                    </Row>
                                    
                                    <div className="import-table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <table className="import-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 50 }}>Row</th>
                                                    <th style={{ width: 100 }}>Status</th>
                                                    {parsedData.headers.map((header, i) => (
                                                        <th key={i}>
                                                            {getFieldIcon(header, parsedData.importableFields?.[header])}
                                                            {LanguageProvider.getLabel(entity, header) || header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {parsedData.rows
                                                    .filter(row => !showOnlyErrors || (row.errors && row.errors.length > 0))
                                                    .map((row, i) => (
                                                    <tr key={i} className={row.errors && row.errors.length > 0 ? 'table-danger' : ''}>
                                                        <td>{row._original_row || i + 1}</td>
                                                        <td>{getRowStatusBadge(row)}</td>
                                                        {parsedData.headers.map((header, j) => (
                                                            <td key={j}>
                                                                {getFieldDisplay(row, header)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    {/* Show errors */}
                                    {parsedData.rows.some(r => r.errors && r.errors.length > 0) && (
                                        <Alert variant="danger" className="mt-3">
                                            <h6>Errors found:</h6>
                                            <ul className="small mb-0">
                                                {parsedData.rows.filter(r => r.errors && r.errors.length > 0).map((row, i) => (
                                                    <li key={i}>
                                                        Row {row._original_row || i + 1}: {row.errors.join(', ')}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                </Container>
                            </>
                        )}
                        
                        {!isSqlView && !loading && step === 3 && importResult && (
                            <>
                                <Container className="mb-4">
                                    <h5>Step 3: Import Complete</h5>
                                    
                                    <Alert variant="success">
                                        <h6>Import Summary:</h6>
                                        <ul className="mb-0">
                                            <li>Records inserted: {importResult.inserted}</li>
                                            <li>Records updated: {importResult.updated}</li>
                                            {importResult.errors && importResult.errors.length > 0 && (
                                                <li>Errors: {importResult.errors.length}</li>
                                            )}
                                        </ul>
                                    </Alert>
                                    
                                    {importResult.errors && importResult.errors.length > 0 && (
                                        <Alert variant="danger">
                                            <h6>Errors:</h6>
                                            <ul className="small mb-0">
                                                {importResult.errors.map((err, i) => (
                                                    <li key={i}>
                                                        Row {err.row}: {err.errors.join(', ')}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                </Container>
                            </>
                        )}
                        
                        {error && (
                            <Alert variant="danger" className='mt-3'>
                                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{error}</pre>
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {isSqlView && (
                            <Button variant="secondary" onClick={closeModal}>
                                {LanguageProvider.get('defaults.close')}
                            </Button>
                        )}
                        
                        {!isSqlView && step === 1 && (
                            <>
                                <Button variant="secondary" onClick={closeModal}>
                                    {LanguageProvider.get('defaults.close')}
                                </Button>
                            </>
                        )}
                        
                        {!isSqlView && step === 2 && (
                            <>
                                <Button variant="warning" onClick={reset}>
                                    <FontAwesomeIcon icon="arrow-left" className="me-2" />
                                    Back
                                </Button>
                                <Button 
                                    variant="success" 
                                    onClick={executeImport}
                                    disabled={!parsedData || parsedData.rows.some(r => r.errors && r.errors.length > 0)}
                                >
                                    <FontAwesomeIcon icon="upload" className="me-2" />
                                    Execute Import
                                </Button>
                                <Button variant="secondary" onClick={closeModal}>
                                    {LanguageProvider.get('defaults.cancel')}
                                </Button>
                            </>
                        )}
                        
                        {!isSqlView && step === 3 && (
                            <>
                                <Button variant="primary" onClick={reset}>
                                    <FontAwesomeIcon icon="plus" className="me-2" />
                                    Import More
                                </Button>
                                <Button variant="secondary" onClick={closeModal}>
                                    {LanguageProvider.get('defaults.close')}
                                </Button>
                            </>
                        )}
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
    
    return null;
};